import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";
import { Upload, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface LorainCountyTextParserProps {
  onImportComplete?: () => void;
}

interface ImportResult {
  successCount: number;
  failureCount: number;
  failedRecords: Array<{ docNumber: string; error: string }>;
}

export const LorainCountyTextParser = ({ onImportComplete }: LorainCountyTextParserProps) => {
  const [textData, setTextData] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const { toast } = useToast();

  const parseDate = (dateStr: string): string | null => {
    if (!dateStr || dateStr.trim() === '') return null;
    
    // Lorain County uses YYYY-MM-DD format
    const match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    
    return null;
  };

  const extractAmount = (amountStr: string): number | null => {
    if (!amountStr || amountStr.trim() === '') return null;
    
    const cleaned = amountStr.replace(/[$,]/g, '').trim();
    const amount = parseFloat(cleaned);
    
    return isNaN(amount) ? null : amount;
  };

  const analyzeOwnerFinance = (
    grantee: string,
    grantor: string
  ): { isOwnerFinance: boolean; score: number; reasons: string[] } => {
    let score = 0;
    const reasons: string[] = [];

    const granteeUpper = grantee.toUpperCase();
    const grantorUpper = grantor.toUpperCase();

    // LLC indicators
    if (granteeUpper.includes('LLC') || granteeUpper.includes('L.L.C.')) {
      score += 30;
      reasons.push('Grantee is LLC');
    }

    // Individual to LLC or business
    const individualPattern = /^[A-Z]+\s+[A-Z]+(\s+[A-Z]+)?$/;
    if (individualPattern.test(grantorUpper) && (granteeUpper.includes('LLC') || granteeUpper.includes('INC'))) {
      score += 25;
      reasons.push('Individual to business entity');
    }

    // Trust to individual
    if (grantorUpper.includes('TRUST') || grantorUpper.includes(' TR')) {
      score += 20;
      reasons.push('Trust involved');
    }

    // Properties/investment companies
    const investmentKeywords = ['PROPERTIES', 'INVESTMENT', 'REALTY', 'CAPITAL', 'HOLDINGS'];
    for (const keyword of investmentKeywords) {
      if (granteeUpper.includes(keyword) || grantorUpper.includes(keyword)) {
        score += 20;
        reasons.push(`Investment entity (${keyword})`);
        break;
      }
    }

    return {
      isOwnerFinance: score >= 30,
      score,
      reasons
    };
  };

  const parseLorainCountyText = (text: string): { records: any[], stats: any } => {
    const lines = text.split('\n').filter(line => line.trim());
    const records: any[] = [];
    
    // Track filtering statistics
    const stats = {
      totalLines: lines.length,
      notLandContract: 0,
      noDocNumber: 0,
      hasMarginalReference: 0,
      underMinAmount: 0,
      passed: 0
    };

    for (const line of lines) {
      const columns = line.split('\t');
      
      // Skip if not enough columns or not a data row
      if (columns.length < 14) continue;

      const rowNum = columns[0]?.trim();
      const party1 = columns[1]?.trim() || '';
      const party1Additional = columns[2]?.trim() || '';
      const role = columns[3]?.trim() || '';
      const party2 = columns[4]?.trim() || '';
      const docType = columns[5]?.trim() || '';
      const docNumber = columns[6]?.trim() || '';
      const filingDateStr = columns[7]?.trim() || '';
      const propertyDesc = columns[8]?.trim() || '';
      const relatedDoc = columns[9]?.trim() || '';
      const amountStr = columns[11]?.trim() || '';
      const status = columns[12]?.trim() || '';
      const notes = columns[13]?.trim() || '';

      // Only process LAND CONTRACT records
      if (docType !== 'LAND CONTRACT') {
        stats.notLandContract++;
        continue;
      }

      // Skip if no document number
      if (!docNumber) {
        stats.noDocNumber++;
        continue;
      }

      // Skip records with marginal references (related documents) - these are inactive contracts
      if (relatedDoc && relatedDoc.trim() !== '') {
        stats.hasMarginalReference++;
        continue;
      }

      // Parse amount first to check minimum threshold
      const loanAmount = extractAmount(amountStr);
      
      // Skip records with consideration amount less than $50,000 (but keep blank amounts)
      if (loanAmount !== null && loanAmount < 50000) {
        stats.underMinAmount++;
        continue;
      }
      
      stats.passed++;

      // Determine grantor and grantee
      let grantor = '';
      let grantee = '';
      
      if (role === 'Grantor') {
        grantor = party1 + (party1Additional ? ' ' + party1Additional : '');
        grantee = party2;
      } else if (role === 'Grantee') {
        grantee = party1 + (party1Additional ? ' ' + party1Additional : '');
        grantor = party2;
      }

      // Parse date (no need to parse amount again, already done above)
      const filingDate = parseDate(filingDateStr);

      // Analyze for owner finance
      const analysis = { isOwnerFinance: true, score: 100, reasons: ['Land Contract'] };

      // Extract property address from description if possible
      let propertyAddress = null;
      const cityMatch = propertyDesc.match(/City:([^T]*?)(?:Twnshp|Sub\/Cndo|Lt|Prcl|Cmnt|Bl|$)/i);
      if (cityMatch) {
        propertyAddress = cityMatch[1].trim();
      }

      records.push({
        county: 'lorain',
        document_number: docNumber,
        document_type: 'LAND CONTRACT',
        grantor_name: grantor || null,
        grantee_name: grantee || null,
        filing_date: filingDate,
        loan_amount: loanAmount,
        property_address: propertyAddress,
        legal_description: propertyDesc || null,
        is_potential_owner_finance: analysis.isOwnerFinance,
        confidence_score: analysis.score,
        detection_reasons: analysis.reasons,
        release_document_number: relatedDoc || null,
        notes: notes || null,
        status: 'new',
        is_active: !relatedDoc || relatedDoc === '',
        address_needs_review: true,
        grantor_address: null,
        raw_data: {
          row_number: rowNum,
          party1,
          party1Additional,
          role,
          party2,
          status,
          property_description: propertyDesc,
          related_document: relatedDoc
        }
      });
    }

    return { records, stats };
  };

  const processText = async (text: string) => {
    setIsProcessing(true);
    setImportResult(null);

    try {
      const { records, stats } = parseLorainCountyText(text);
      
      console.log('Lorain County Import Filter Statistics:', stats);
      
      if (records.length === 0) {
        toast({
          title: "No Records Found",
          description: "No land contract records were found in the pasted data.",
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }

      setProgress({ current: 0, total: records.length });

      let successCount = 0;
      let failureCount = 0;
      const failedRecords: Array<{ docNumber: string; error: string }> = [];

      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        setProgress({ current: i + 1, total: records.length });

        try {
          // Check for duplicates
          const { data: existing } = await supabase
            .from('county_research')
            .select('id')
            .eq('county', 'lorain')
            .eq('document_number', record.document_number)
            .maybeSingle();

          if (existing) {
            console.log(`Skipping duplicate: ${record.document_number}`);
            continue;
          }

          // Insert new record
          const { error } = await supabase
            .from('county_research')
            .insert(record);

          if (error) throw error;
          successCount++;
        } catch (error: any) {
          console.error(`Failed to import ${record.document_number}:`, error);
          failureCount++;
          failedRecords.push({
            docNumber: record.document_number,
            error: error.message
          });
        }
      }

      if (successCount > 0) backupRecordsToExcel(records, 'lorain', 'lorain_parser');
      setImportResult({ successCount, failureCount, failedRecords });

      const filterMessage = `
        Filtered: ${stats.notLandContract} (not land contract), 
        ${stats.hasMarginalReference} (marginal references), 
        ${stats.underMinAmount} (under $50k), 
        ${stats.noDocNumber} (no doc #)
      `;

      toast({
        title: "Import Complete",
        description: `Successfully imported ${successCount} of ${stats.passed} qualified records. ${failureCount} failed. ${filterMessage}`,
      });

      if (onImportComplete) {
        onImportComplete();
      }

      // Clear the text area after successful import
      if (successCount > 0) {
        setTextData("");
      }
    } catch (error: any) {
      console.error('Import error:', error);
      toast({
        title: "Import Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result as string;
      setTextData(text);
      await processText(text);
    };
    reader.readAsText(file);
  };

  const handlePasteImport = async () => {
    if (!textData.trim()) {
      toast({
        title: "No Data",
        description: "Please paste data into the text area first.",
        variant: "destructive",
      });
      return;
    }

    await processText(textData);
  };

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">Lorain County Text Import</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Paste tab-separated data from Lorain County records or upload a text file.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Upload Text File
              </label>
              <input
                type="file"
                accept=".txt"
                onChange={handleFileUpload}
                disabled={isProcessing}
                className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Or Paste Data Here
              </label>
              <Textarea
                value={textData}
                onChange={(e) => setTextData(e.target.value)}
                placeholder="Paste tab-separated data from Lorain County here..."
                className="min-h-[200px] font-mono text-xs"
                disabled={isProcessing}
              />
            </div>

            <Button
              onClick={handlePasteImport}
              disabled={isProcessing || !textData.trim()}
              className="w-full"
            >
              <Upload className="w-4 h-4 mr-2" />
              {isProcessing ? "Processing..." : "Import Data"}
            </Button>

            {isProcessing && progress.total > 0 && (
              <div className="space-y-2">
                <div className="text-sm text-muted-foreground">
                  Processing {progress.current} of {progress.total} records...
                </div>
                <div className="w-full bg-secondary rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {importResult && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <div>
                <strong>Import Results:</strong>
              </div>
              <div>Successfully imported: {importResult.successCount} records</div>
              {importResult.failureCount > 0 && (
                <div>
                  <div className="text-destructive">
                    Failed: {importResult.failureCount} records
                  </div>
                  <div className="mt-2 text-xs">
                    {importResult.failedRecords.slice(0, 5).map((record, idx) => (
                      <div key={idx}>
                        {record.docNumber}: {record.error}
                      </div>
                    ))}
                    {importResult.failedRecords.length > 5 && (
                      <div>...and {importResult.failedRecords.length - 5} more</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <div className="space-y-2 text-sm">
            <div><strong>Expected Format:</strong></div>
            <div>Tab-separated data with columns: Row#, Party1, Additional, Role, Party2, DocType, DocNumber, FilingDate, PropertyDesc, RelatedDoc, (empty), Amount, Status, Notes</div>
            <div className="text-xs text-muted-foreground mt-2">
              The parser will automatically identify land contracts and analyze them for owner financing patterns.
            </div>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
};
